// نسخه — the pure half. No React, no text, no network.
//
// It exists separately from the screen because three different things have to
// agree about what a prescription row IS: what the interaction sweep is asked
// about, what gets written to clinicalencounter, and what the dentist reads back on
// the patient's سوابق tab. One shape, defined here, tested directly.
//
// The one rule that is easy to get wrong and impossible to see in review:
// the interaction sweep is keyed on `Drug.id` — a stable slug like
// 'acetaminophen' — and NEVER on the Persian display name. The rule engine
// keys its static pairs on those slugs, so a name reaches it as an unknown drug
// and comes back clean.

import { todayInIran } from './iranTime.js';

/** A stable client-side row id. The medication has no server id until it is saved. */
let seq = 0;
const nextKey = () => `rx-${++seq}`;

/**
 * A new prescription row from a formulary hit.
 *
 * Dose and frequency are pre-filled from the formulary's first common value
 * because that is the answer in the majority of cases and a wrong pre-fill is
 * visible in the field — an empty field costs two keystrokes every time.
 */
export function newRxItem(drug) {
  return {
    key: nextKey(),
    drug,
    dose: drug?.commonDosages?.[0] || '',
    frequency: drug?.commonFrequencies?.[0] || '',
    duration: '',
    note: '',
  };
}

/**
 * The human dosage line, e.g. «۵۰۰ میلی‌گرم · روزی دو بار · ۷ روز».
 * Blank parts are dropped rather than rendered as empty separators.
 */
export function dosageText(item) {
  return [item?.dose, item?.frequency, item?.duration, item?.note]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' · ');
}

/**
 * A row → the BFF's MedicationInput.
 *
 * `code` is the drug slug and `display` the name the dentist chose, so a saved
 * medication can still be matched back to the formulary entry it came from.
 * `dosageInstruction` is JSON: the BFF writes it as a JSON string and the
 * service returns it as a struct — the asymmetry is handled server-side, but it
 * is why this must be an OBJECT here and never a pre-stringified blob.
 */
export function toMedicationInput(item, { appointmentId = null, authoredOn } = {}) {
  const text = dosageText(item);
  return {
    code: item?.drug?.id || '',
    display: item?.drug?.name || item?.drug?.genericName || '',
    status: 'active',
    authoredOn: authoredOn || todayInIran(),
    dosageInstruction: {
      text,
      dose: item?.dose || '',
      frequency: item?.frequency || '',
      duration: item?.duration || '',
      note: item?.note || '',
    },
    ...(appointmentId ? { appointmentId } : {}),
  };
}

/** The slugs to sweep — deduped, blanks dropped. */
export function interactionIds(items) {
  const seen = new Set();
  for (const item of items || []) {
    const id = item?.drug?.id;
    if (id) seen.add(id);
  }
  return [...seen];
}

/**
 * Whether a sweep is worth a call. Below two distinct drugs there is nothing to
 * interact, and the sweep COSTS PARSINA CREDITS — a one-drug call spends real
 * money to be told "no interactions".
 */
export function canSweep(items) {
  return interactionIds(items).length >= 2;
}

/** Severity, folded to the three levels the UI paints. Unknown → 'low'. */
export function severityKey(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (['high', 'severe', 'major', 'critical', 'contraindicated'].includes(s)) return 'high';
  if (['moderate', 'medium', 'significant'].includes(s)) return 'moderate';
  return 'low';
}

const SEVERITY_RANK = { high: 0, moderate: 1, low: 2 };

/** Warning types the catalogue has copy for. Anything else renders untyped. */
export const WARNING_TYPES = ['interaction', 'contraindication', 'allergy', 'black_box'];

export function warningTypeKey(raw) {
  const s = String(raw || '').toLowerCase().trim();
  return WARNING_TYPES.includes(s) ? s : null;
}

/** Most severe first; stable within a level so the list does not shuffle on refresh. */
export function sortWarnings(warnings) {
  return [...(warnings || [])]
    .map((w, i) => ({ w, i, rank: SEVERITY_RANK[severityKey(w?.severity)] ?? 2 }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.w);
}

/** Allergen strings for the sweep — the patient's own recorded allergies. */
export const allergenNames = (patient) =>
  (patient?.allergies || []).map((a) => a?.allergen).filter(Boolean);

/** Condition display names for the sweep. */
export const conditionNames = (patient) =>
  (patient?.conditions || []).map((c) => c?.display).filter(Boolean);
